import { Capacitor } from '@capacitor/core';
import {
  Grid,
  Chip,
  Typography,
  Box,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { DrawerStickyHeader } from 'components/DrawerStickyHeader';
import { LoadingAndErrors } from 'components/LoadingAndErrors';
import { MapEntityDrawerContext } from 'components/Map/MapEntityDrawer';
import { MapEntityDrawerHeader } from 'components/Map/MapEntityDrawerHeader';
import { SupportUsBanner } from 'components/SupportUsBanner';
import { useGeoEventQuery } from 'features/geoEvents/hooks/useGeoEventQuery';
import { WildfireAutomatedDisclaimer } from 'features/wildfires/components/WildfireAutomatedDisclaimer';
import { WildfireEvacuationInfo } from 'features/wildfires/components/WildfireEvacuationInfo';
import { WildfireEvacuationsButton } from 'features/wildfires/components/WildfireEvacuationsButton';
import { WildfireGeneralInfo } from 'features/wildfires/components/WildfireGeneralInfo';
import { WildfireLocations } from 'features/wildfires/components/WildfireLocations';
import { WildfireReports } from 'features/wildfires/components/WildfireReports';
import { WildfireStructuredInfo } from 'features/wildfires/components/WildfireStructuredInfo';
import { useWildfireBanner } from 'features/wildfires/hooks/useWildfireBanner';
import { useTabs } from 'hooks/useTabs';
import { useContext, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { ChildLocation } from 'features/locations/location.types';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { getRegionsDisplay } from 'shared/utils';
import { useSelectedWildfireGeoEventId } from 'features/wildfires/hooks/useSelectedWildfireGeoEventId';

const TAB_PANEL_ID_PREFIX = 'incident-details';

const getChildLocations = (geoEvent: Wildfire): ChildLocation[] =>
  geoEvent.childGeoEvents.filter(
    (ge) => ge.geoEventType === 'location',
  ) as ChildLocation[];

export const WildfireDetails = (): JSX.Element => {
  const geoEventId = useSelectedWildfireGeoEventId();
  const { geoEvent, isLoading, isError } = useGeoEventQuery<Wildfire>();
  const theme = useTheme();
  const isLargeMediaQuery = useMediaQuery(theme.breakpoints.up(660), {
    defaultMatches: !Capacitor.isNativePlatform(),
  });
  const { t } = useTranslation();
  const { showBanner, handleClose } = useWildfireBanner({ wildfire: geoEvent });

  const numShelters =
    (geoEvent &&
      geoEvent.childGeoEvents.filter((ge) => ge.geoEventType === 'location')
        .length) ||
    0;

  const { scrollElementRef } = useContext(MapEntityDrawerContext);

  const [stickyHeaderState, setStickyHeaderState] = useState<{
    isFixed: boolean;
    contentHeight: number;
  }>({ isFixed: false, contentHeight: 0 });
  const [evacTopBtn, setEvacTopBtn] = useState<boolean>(false);

  const tabData = [
    {
      tabId: 'updates',
      label: t('geoEvent.tabs.updates'),
      enabled: true,
      content: geoEventId && <WildfireReports wildfireId={geoEventId} />,
    },
    {
      tabId: 'shelters',
      label: t('geoEvent.tabs.shelters'),
      labelChip: numShelters,
      enabled: numShelters > 0,
      content: geoEvent && (
        <WildfireLocations locations={getChildLocations(geoEvent)} />
      ),
    },
    {
      tabId: 'info',
      label: t('geoEvent.tabs.info'),
      enabled: true,
      content: geoEvent && <WildfireStructuredInfo wildfire={geoEvent} />,
    },
  ];

  const { tabsProps, tabProps, tabPanels } = useTabs({
    idPrefix: TAB_PANEL_ID_PREFIX,
    ariaLabel: t('geoEvent.tabsLabel'),
    tabData,
  });

  const handleTop = (): void => {
    if (!scrollElementRef) {
      return;
    }

    if (isLargeMediaQuery) {
      scrollElementRef.current?.scrollTo({ behavior: 'smooth', top: 0 });
    } else {
      scrollElementRef.current?.scrollTo({ behavior: 'smooth', top: 0 });
    }
  };

  const handleStickyHeaderContentHeightChange = useCallback(
    (contentHeight: number) => {
      setStickyHeaderState((prev) => ({ ...prev, contentHeight }));
    },
    [],
  );

  if (isLoading) {
    return <LoadingAndErrors isLoading />;
  }

  if (isError || !geoEvent || !geoEventId) {
    return <LoadingAndErrors />;
  }

  const complexName = geoEvent.parentGeoEvents?.[0]?.name;

  return (
    <>
      <Helmet>
        <title>{t('home.wildfireTitle', { name: geoEvent.name })}</title>
      </Helmet>

      <Grid container>
        <Grid item xs={12}>
          <DrawerStickyHeader
            scrollContainer={scrollElementRef?.current as HTMLElement}
            onStickyChange={(isFixed) =>
              setStickyHeaderState((prev) => ({ ...prev, isFixed }))
            }
            onStickyContentHeightChange={handleStickyHeaderContentHeightChange}
          >
            <DrawerStickyHeader.ContentContainer>
              <MapEntityDrawerHeader>
                {geoEvent.name}
                {complexName && (
                  <Chip
                    variant="watchduty"
                    label={complexName}
                    sx={{
                      marginLeft: 1,
                      backgroundColor: 'watchdutyComplex.main',
                    }}
                  />
                )}
              </MapEntityDrawerHeader>

              {Boolean(geoEvent.address) && !stickyHeaderState.isFixed && (
                <Typography data-testid="geoevent-address">
                  {geoEvent.address}
                </Typography>
              )}

              <Typography variant="body2" color="textSecondary">
                {getRegionsDisplay(geoEvent.regions)}
              </Typography>
            </DrawerStickyHeader.ContentContainer>
          </DrawerStickyHeader>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ padding: '0px 16px 16px' }}>
            <WildfireGeneralInfo wildfire={geoEvent} />
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ padding: '0px 16px 16px' }}>
            {geoEvent.reporterManaged ? (
              <WildfireEvacuationInfo wildfire={geoEvent} />
            ) : (
              <WildfireAutomatedDisclaimer wildfire={geoEvent} />
            )}
          </Box>
        </Grid>

        {showBanner && (
          <Grid item xs={12}>
            <Box sx={{ padding: '0px 16px 16px' }}>
              <SupportUsBanner
                mode="dark"
                geoEventId={geoEventId}
                onClose={handleClose}
              />
            </Box>
          </Grid>
        )}

        <Grid item xs={12}>
          <DrawerStickyHeader
            scrollContainer={scrollElementRef?.current as HTMLElement}
            topOffset={stickyHeaderState.contentHeight * -1}
            stickyStyle={{ marginTop: stickyHeaderState.contentHeight }}
            onStickyChange={(isVisible) => setEvacTopBtn(isVisible)}
          >
            {evacTopBtn && (
              <WildfireEvacuationsButton
                wildfire={geoEvent}
                handleTop={handleTop}
              />
            )}

            <Tabs {...tabsProps}>
              {tabProps.map((props) => (
                <Tab
                  key={props.key}
                  {...props}
                  sx={{
                    maxWidth: 1 / tabData.filter((tab) => tab.enabled).length,
                  }}
                />
              ))}
            </Tabs>
          </DrawerStickyHeader>
        </Grid>
      </Grid>

      {tabPanels}
    </>
  );
};
